import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import MoneyInput from '../components/MoneyInput'
import { formatMoney } from '../lib/money'
import { drawProblem } from '../lib/provisional'
import type { CostProvisionalSummary } from '../types'

interface Props {
  /** The provisional line to draw from; null closes the dialog. */
  row: CostProvisionalSummary | null
  currency: string
  locale: string
  onClose: () => void
  /** Resolves true once the change exists (the page then opens it); the dialog stays open on failure. */
  onSubmit: (title: string, amount: string) => Promise<boolean>
}

/** Part D spec §2.4: a title and an amount (default: everything that remains) for the change that draws from the reserve. */
const DrawProvisionalDialog: React.FC<Props> = ({ row, currency, locale, onClose, onSubmit }) => {
  const { t } = useTranslation()
  const [title, setTitle] = useState('')
  const [amount, setAmount] = useState<string | null>(null)
  const [valid, setValid] = useState(true)
  const [touched, setTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!row) return
    setTitle(t('CostControl_provisional_draw_default_title', { item: row.nodeName }))
    setAmount(row.remaining)
    setValid(true)
    setTouched(false)
    setSubmitting(false)
    // Only on opening: a background reload of the contract must not wipe what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?.nodeId])

  const problem = row ? drawProblem(title, amount, valid, row.remaining) : null
  const submit = async () => {
    setTouched(true)
    if (!row || problem || submitting) return
    setSubmitting(true)
    const ok = await onSubmit(title.trim(), amount as string)
    setSubmitting(false)
    if (ok) onClose()
  }

  return (
    <Dialog open={!!row} onOpenChange={(next) => { if (!next && !submitting) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('CostControl_provisional_draw')}</DialogTitle>
          <DialogDescription>{t('CostControl_provisional_draw_hint')}</DialogDescription>
        </DialogHeader>
        <form className="grid gap-4" onSubmit={(e) => { e.preventDefault(); submit() }}>
          <div className="space-y-1.5">
            <Label htmlFor="cc-draw-title">{t('CostControl_change_field_title')} *</Label>
            <Input id="cc-draw-title" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-draw-amount">{`${t('CostControl_provisional_draw_amount')} (${currency})`}</Label>
            <MoneyInput id="cc-draw-amount" value={amount} onChange={(v, ok) => { setAmount(v); setValid(ok) }} />
            {row && (
              <p className="text-xs text-muted-foreground">
                {t('CostControl_provisional_col_remaining')}: <span className="tabular-nums">{formatMoney(row.remaining, { locale, currency })}</span>
              </p>
            )}
          </div>
          {touched && problem && <p className="text-sm text-destructive">{t(problem)}</p>}
          <div className="flex items-center justify-end gap-2 border-t pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>{t('Common_cancel')}</Button>
            <Button type="submit" disabled={submitting}>
              {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {t('Common_create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
export default DrawProvisionalDialog
